import { Box, SelectChangeEvent } from '@mui/material';
import StyledMain from '../../dashboard/styled/StyledMain';
import { Typography } from 'src/components/new/Typography';
import useAdminTable from './useAdminTable';
import ReportsTable from './reports-components/ReportsTable';
import JobHistoryFilters from './reports-components/JobHistoryFilters';
import { useEffect, useState } from 'react';
import { DashboardFilters, organizationTypes } from 'src/utils/constant';
import { Button } from 'src/components/new/Button';
import BkDropdownMenu from 'src/components/dropdown-menu/BkDropdownMenu';
import IosShareIcon from '@mui/icons-material/IosShare';
import { formatDateForFiltering } from 'src/utils/utility-functions';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import QDirectRecoveryDialog from 'src/components/modals/QDirectRecoveryDialog';
import { apiInstance } from 'src/api/apiInstance';
import { downloadRecoveryCsv, RecoveryItem } from 'src/utils/qdirectRecoveryCsv';

const STATUSES = [
  {
    label: 'In Queue',
    id: 'in_queue',
  },
  {
    label: 'Processing',
    id: 'processing',
  },
  {
    label: 'Printing',
    id: 'printing',
  },
  {
    label: 'Complete',
    id: 'completed',
  },
  {
    label: 'Complete - Payment Declined',
    id: 'completed_payment_declined',
  },
  {
    label: 'On Hold',
    id: 'on_hold',
  },
  {
    label: 'Cancelled',
    id: 'cancelled',
  },
  {
    label: 'Manual Review',
    id: 'manual_review',
  },
];

const NOTICE_TYPES = [
  {
    label: 'Bankrupcty',
    id: 'bankruptcy notice',
  },
  {
    label: 'Legal',
    id: 'legal',
  },
  {
    label: 'Direct',
    id: 'direct mailing',
  },
];

const ON_HOLD_REASONS = [
  { label: 'Payment Declined', id: 'Payment Declined' },
  { label: 'Manual', id: 'Manual' },
];

const SERVICE_OPTIONS = [
  { label: 'Normal', id: 'normal' },
  { label: 'Rush', id: 'rush' },
];

export const JobHistoryPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const isCosAdmin = currentUser.teamRole === 'admin';

  const [selectedFilters, setSelectedFilters] = useState<DashboardFilters>({
    noticeType: '',
    status: [],
    onHoldReason: [],
    service: [],
    createdAt_start: '',
    createdAt_end: '',
    searchValue: '',
    printGenStatus: '',
  });

  const {
    data,
    loading,
    error,
    openExportButton,
    exportAnchor,
    exportItems,
    closeExportButton,
    updateData,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalCount,
    errorMsg,
    clearErrorMsg,
  } = useAdminTable({
    activityType: 'history',
    filters: selectedFilters,
  });

  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);

  const handleOpenRecoveryDialog = () => {
    setRecoveryDialogOpen(true);
  };

  const handleCloseRecoveryDialog = () => {
    setRecoveryError(null);
    setRecoveryDialogOpen(false);
  };

  const handleConfirmRecovery = async () => {
    setRecoveryLoading(true);
    setRecoveryError(null);
    try {
      const response = await apiInstance.post<RecoveryItem[]>('/job/qdirect-recovery');
      downloadRecoveryCsv(response.data);
      setRecoveryDialogOpen(false);
      setRecoveryLoading(false);
    } catch {
      setRecoveryError('Recovery failed. Please try again.');
      setRecoveryLoading(false);
    }
  };

  const [searchTerm, setSearchTerm] = useState(selectedFilters.searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        searchValue: searchTerm,
      }));
      setPage(0);
    }, 1000);

    return () => clearTimeout(handler);
  }, [searchTerm]); // debounce search value

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event: SelectChangeEvent<string | (string | null)[]>) => {
    if (Array.isArray(event)) {
      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        createdAt_start: (event as any)[0] ? formatDateForFiltering(event[0] as any, 'start') : '',
        createdAt_end: (event as any)[1] ? formatDateForFiltering(event[1] as any, 'end') : '',
      }));
    } else {
      const { name, value } = event.target;

      setSelectedFilters((prevFilters) => ({
        ...prevFilters,
        [name]: value,
      }));
    }
    setPage(0);
  };

  return (
    <Box display="flex" flexDirection="row" height={'100vh'}>
      <BkSnackBar snackMessage={errorMsg} open={!!errorMsg} handleCloseSnackbar={clearErrorMsg} />
      <StyledMain open={true}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
          }}
        >
          <Typography
            sx={{ fontFamily: 'ABC Arizona Mix', fontWeight: 300, fontSize: '2.8125rem', lineHeight: '3.25rem' }}
          >
            Job History
          </Typography>
        </Box>
        <Box sx={{ width: '100%', padding: '1.5rem' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2.1875rem' }}>
            <JobHistoryFilters
              noticeTypes={NOTICE_TYPES}
              statuses={STATUSES}
              onHoldReasons={ON_HOLD_REASONS}
              serviceOptions={SERVICE_OPTIONS}
              orgTypes={organizationTypes.map((item) => ({ label: item, id: item }))}
              onFilterChange={handleFilterChange}
              selectedFilters={selectedFilters}
              handleSearchChange={handleSearchChange}
              searchTerm={searchTerm}
            />
            {isCosAdmin && (
              <Button
                type="outline"
                onClick={handleOpenRecoveryDialog}
                sx={{
                  width: '9.375rem',
                  height: 'auto',
                  backgroundColor: '#ffeedf',
                  color: '#7B3F07',
                  borderColor: '#7B3F07',
                  fontSize: '0.7rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  '&:hover': {
                    backgroundColor: '#ffe4cc',
                    borderColor: '#7B3F07',
                  },
                }}
              >
                Initiate QDirect Recovery
              </Button>
            )}
            <Button
              startIcon={<IosShareIcon />}
              onClick={openExportButton}
              type="outline"
              sx={{ width: '9.375rem' }}
            >
              Export
            </Button>
            <BkDropdownMenu
              anchorEl={exportAnchor}
              open={!!exportAnchor}
              onClose={closeExportButton}
              menuItems={exportItems}
            />
          </Box>
          <ReportsTable
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            totalCount={totalCount}
            updateData={updateData}
            showPagination={true}
            data={data}
          />
        </Box>
      </StyledMain>
      <QDirectRecoveryDialog
        open={recoveryDialogOpen}
        onClose={handleCloseRecoveryDialog}
        onConfirm={handleConfirmRecovery}
        loading={recoveryLoading}
        error={recoveryError}
      />
    </Box>
  );
};
